// pipe: 4B.8 (Task 11) — the PURE, prop-only driver "My Pickups" list view (the
// Vitest-tested seam). UI-polish: a touch-friendly mobile CARD list (was a dense
// desktop <Table>), rendered inside the shared <DriverScreen> frame, with the
// per-row CTA as the @affine/component Button.
//
// Import discipline (mirrors orders-view.tsx): this file imports ONLY
// @affine/component (Button — resolves under happy-dom), @affine/i18n (mocked
// under happy-dom), the shared driver-screen frame + css modules, and a TYPE-ONLY
// import from the driver erp service (erased by swc — no runtime load). It must
// NOT pull the runtime DriverErpService, the auth/PKCE plumbing, react-router, or
// any DI. The container (pickups.tsx) owns all of that.
//
// THE LOAD-BEARING INVARIANT (CLAUDE.md 4B.6): the "Complete pickup" affordance
// renders STRICTLY from the server-computed `can_complete` flag. The view NEVER
// inspects `data.status` to decide whether a pickup can be completed — the
// server is the single authority for that ACT.
// Subpath imports (the package exports map supports them) so the lean driver
// bundle pulls only these modules, never the whole @affine/component barrel.
import { Button } from '@affine/component/ui/button';
import { Loading } from '@affine/component/ui/loading';
import { useI18n } from '@affine/i18n';

import type { DriverPickup } from '../services/driver-erp';
import { DriverScreen } from './driver-screen';
import * as ui from './driver-ui.css';
import * as styles from './pickups.css';

export interface PickupsViewProps {
  /** The driver's assigned pickups (Driver redact projection); empty while
   *  loading or after an error. */
  rows: DriverPickup[];
  loading?: boolean;
  error?: string | null;
  /** Begin completing one pickup. The container owns navigation to the
   *  pickup-complete flow (`/pickups/:id`, Task 12). */
  onComplete: (id: string) => void;
  /** P3.4: open the trip-log form for one pickup. The affordance renders only
   *  when the container wires this (prop-gated — every row may log a trip). */
  onTripLog?: (id: string) => void;
}

// `data` is `Record<string, unknown>` (the Driver projection) — read every field
// DEFENSIVELY. Never reference facility_id/stage/allowed_transitions (omitted by
// the Driver serializer — they don't exist).
function str(v: unknown): string {
  return v === undefined || v === null ? '—' : String(v);
}

// A human-readable lifecycle status for the chip: the raw status is snake_case
// (e.g. `picked_up`); show spaced words ("picked up"), and the chip's
// `textTransform: capitalize` handles the casing. Tokens/CSS own presentation.
function statusLabel(v: unknown): string {
  return str(v).replace(/_/g, ' ');
}

// A short, human-scannable id fallback when the row carries no title.
function shortId(id: string): string {
  return id.slice(0, 8);
}

// Driver-facing dates via Intl/CLDR; `en-US` keeps the assertions stable. The
// real locale is applied by the driver i18n layer.
const df = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' });

function created(row: DriverPickup): string {
  const d = new Date(row.created_at);
  return Number.isNaN(d.getTime()) ? '—' : df.format(d);
}

export function PickupsView(props: PickupsViewProps) {
  const t = useI18n();

  if (props.error) {
    return (
      <DriverScreen>
        <div className={ui.errorPanel} role="alert">
          {t['com.affine.pipe.driver.pickups.error']()}
        </div>
      </DriverScreen>
    );
  }

  const title = t['com.affine.pipe.driver.pickups.title']();

  if (props.rows.length === 0) {
    return (
      <DriverScreen title={title}>
        <div className={ui.statusPanel} role="status">
          {props.loading ? <Loading size={24} /> : null}
          {props.loading
            ? t['com.affine.pipe.driver.pickups.loading']()
            : t['com.affine.pipe.driver.pickups.empty']()}
        </div>
      </DriverScreen>
    );
  }

  return (
    <DriverScreen title={title}>
      <ul className={styles.cardList}>
        {props.rows.map(row => (
          <li key={row.id} className={styles.card}>
            <div className={styles.cardHead}>
              <span className={styles.cardTitle}>
                {row.title ?? shortId(row.id)}
              </span>
              <div className={styles.cardMeta}>
                <span className={styles.statusChip}>
                  {statusLabel(row.data.status)}
                </span>
                <span className={styles.cardDate}>{created(row)}</span>
              </div>
            </div>
            {/* STRICTLY server-driven: the affordance exists iff the server says
                `can_complete`. NEVER re-derived from `data.status` client-side
                (CLAUDE.md 4B.6). A non-completable pickup shows its status only. */}
            {row.can_complete ? (
              <Button
                variant="primary"
                size="large"
                block
                onClick={() => props.onComplete(row.id)}
              >
                {t['com.affine.pipe.driver.pickups.complete']()}
              </Button>
            ) : null}
            {/* P3.4: every assigned pickup may carry a trip log (times/odometers
                span the whole run, not only the completable window). */}
            {props.onTripLog ? (
              <Button
                variant="secondary"
                size="large"
                block
                onClick={() => props.onTripLog?.(row.id)}
              >
                {t['com.affine.pipe.driver.trip.title']()}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </DriverScreen>
  );
}
